/* oxlint-disable no-restricted-imports -- theme defaults extend the Mantine components themselves */
import type { ReactNode } from 'react';
import {
  ActionIcon,
  Alert,
  Anchor,
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  CheckboxCard,
  Chip,
  createTheme,
  defaultVariantColorsResolver,
  getDefaultZIndex,
  Highlight,
  HoverCard,
  Input,
  InputWrapper,
  MantineProvider,
  Menu,
  Modal,
  Paper,
  Popover,
  Radio,
  RadioCard,
  SegmentedControl,
  Stepper,
  Tabs,
  Textarea,
} from '@mantine/core';
import type { MantineThemeOverride } from '@mantine/core';

import { colors, convertToColorArrays, otherColors } from '@highlight-cp/colors';

import actionIconClasses from './action-icon.module.css';
import alertClasses from './alert.module.css';
import anchorClasses from './anchor.module.css';
import avatarClasses from './avatar.module.css';
import badgeClasses from './badge.module.css';
import buttonClasses from './button.module.css';
import cardClasses from './card.module.css';
import choiceControlClasses from './choice-control.module.css';
import { cssVariablesResolver } from './css-variables-resolver';
import inputClasses from './input.module.css';
import menuClasses from './menu.module.css';
import modalClasses from './modal.module.css';
import paperClasses from './paper.module.css';
import radioCardClasses from './radio-card.module.css';
import stepperClasses from './stepper.module.css';
import tabsClasses from './tabs.module.css';
import textAreaClasses from './text-area.module.css';

//? https://mantine.dev/theming/theme-object/

export interface CommunityThemeOverride {
  primary: string;
  slug: string;
  privacyPolicyUrl?: string | null;
}

const generateShade = ({
  r,
  g,
  b,
  factor,
}: {
  r: number;
  g: number;
  b: number;
  factor: number;
}) => {
  let newR = r;
  let newG = g;
  let newB = b;

  if (factor < 0) {
    // Lighten by blending towards white
    newR = Math.round(r + (255 - r) * Math.abs(factor));
    newG = Math.round(g + (255 - g) * Math.abs(factor));
    newB = Math.round(b + (255 - b) * Math.abs(factor));
  } else if (factor > 0) {
    // Darken by blending towards black
    newR = Math.round(r * (1 - factor));
    newG = Math.round(g * (1 - factor));
    newB = Math.round(b * (1 - factor));
  }

  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

/**
 * Generate a color palette from a single hex color by creating lighter and darker shades.
 * Blends the color towards white (lighter) and black (darker).
 */
function generateColorShades(
  hexColor: string,
): [string, string, string, string, string, string, string, string, string, string] {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Generate 10 shades where index 5 is the original / primary color
  // Index 0-4: blending towards white (lighter)
  // Index 5: original color
  // Index 6-9: blending towards black (darker)
  return [
    generateShade({ factor: -0.8, r, g, b }), // 50: very light
    generateShade({ factor: -0.6, r, g, b }), // 100: light
    generateShade({ factor: -0.4, r, g, b }), // 200: light-medium
    generateShade({ factor: -0.2, r, g, b }), // 300: medium-light
    generateShade({ factor: -0.1, r, g, b }), // 400: slightly lighter
    generateShade({ factor: 0.0, r, g, b }), // 500: original color
    generateShade({ factor: 0.1, r, g, b }), // 600: slightly darker
    generateShade({ factor: 0.3, r, g, b }), // 700: medium-dark
    generateShade({ factor: 0.5, r, g, b }), // 800: dark
    generateShade({ factor: 0.7, r, g, b }), // 900: very dark
  ];
}

interface ThemeProps {
  children: ReactNode;
  fontFamily?: string;
  fontFamilyHeadings?: string;
  fontFamilyMonospace?: string;
  communityThemeOverride?: CommunityThemeOverride | null;
  /**
   * CSS selector the Mantine CSS variables are attached to. Defaults to `:root`.
   * Pass `:host` when rendering inside a Shadow DOM embed so the variables are
   * scoped to the shadow root instead of leaking to the host page.
   */
  cssVariablesSelector?: string;
  /**
   * Render Mantine's CSS-variables `<style>` tag (default `true`). Test
   * wrappers pass `false` so assertions like `container.firstChild` still see
   * the component under test rather than the injected style element.
   */
  withCssVariables?: boolean;
  /**
   * Render Mantine's responsive utility classes `<style>` tag (default `true`).
   * Test wrappers pass `false` for the same reason as {@link withCssVariables}.
   */
  withGlobalClasses?: boolean;
}

function getColors(
  communityThemeOverride: CommunityThemeOverride | null,
): Record<
  string,
  [string, string, string, string, string, string, string, string, string, string]
> {
  const baseColors = convertToColorArrays(colors);

  if (!communityThemeOverride) {
    return baseColors;
  }

  return {
    ...baseColors,
    primary: generateColorShades(communityThemeOverride.primary),
  };
}

interface BuildThemeOptions {
  fontFamily?: string;
  fontFamilyHeadings?: string;
  fontFamilyMonospace?: string;
  communityThemeOverride?: CommunityThemeOverride | null;
}

/**
 * Builds the Mantine theme override the app mounts. Exported so tests can
 * assert against the exact theme the provider uses (see
 * css-variables-resolver.test.ts) instead of a drifting copy.
 */
export function buildTheme(options: BuildThemeOptions = {}): MantineThemeOverride {
  const {
    fontFamily = 'Inter, sans-serif',
    fontFamilyMonospace = 'Inter, sans-serif',
    fontFamilyHeadings = 'Poppins, sans-serif',
    communityThemeOverride = null,
  } = options;

  const themeColors = getColors(communityThemeOverride);

  return createTheme({
    colors: themeColors,
    primaryColor: 'primary',
    // `variant="tinted"` is Avatar's soft two-shade treatment (see avatar.module.css): the -2 fill
    // inside a -3 ring, with dimmed text. Mantine's `light` fill is the -light semantic tint, a
    // shade or two paler than the Figma facepile, and it has no ring. Dark flips to the deep
    // -9/-8 pair, the way the `-light` tints do, so the dimmed text stays readable. Every other
    // variant defers to Mantine's own resolver.
    variantColorResolver: (input) => {
      if (input.variant !== 'tinted') {
        return defaultVariantColorsResolver(input);
      }
      const color = input.color || 'gray';
      return {
        background: `light-dark(var(--mantine-color-${color}-2), var(--mantine-color-${color}-9))`,
        hover: `light-dark(var(--mantine-color-${color}-3), var(--mantine-color-${color}-8))`,
        color: 'var(--mantine-color-dimmed)',
        border: `2px solid light-dark(var(--mantine-color-${color}-3), var(--mantine-color-${color}-8))`,
      };
    },
    // dark: 5 (not Mantine's default 8) so the brand fill stays visible on the
    // near-black body and filled buttons keep white text at the same contrast
    // they already ship in light (primary-5 + white). Foreground primary text
    // (anchor, *-light-color) is lightened separately in the resolver.
    primaryShade: { light: 5, dark: 5 },

    // Default text color; matches gray-9 so text without an explicit color renders the same as core-ui Text's forced default
    black: '#252B35',

    fontFamily,
    fontFamilyMonospace,

    headings: {
      fontFamily: fontFamilyHeadings,
    },

    other: {
      ...otherColors,
      slug: communityThemeOverride?.slug || null,
      privacyPolicyUrl: communityThemeOverride?.privacyPolicyUrl ?? null,
    } satisfies HighlightThemeOther,

    components: {
      // Skins that used to be per-call-site `className`s on the locked ActionIcon (see
      // action-icon.module.css). Keyed on `mod` data attributes instead of `variant`, because
      // Mantine's `ActionIconVariant` is a closed union — a custom variant string won't typecheck.
      ActionIcon: ActionIcon.extend({
        classNames: { root: actionIconClasses.root },
      }),
      // The callout the designs use: wider sides, the icon closer to its text, and a larger
      // semibold title with the message tucked under it. A call site's `p`/`px`/`py` still wins.
      Alert: Alert.extend({
        classNames: {
          root: alertClasses.root,
          icon: alertClasses.icon,
          body: alertClasses.body,
          title: alertClasses.title,
        },
      }),
      // The shared link skin (see anchor.module.css): the icon row core-ui's Link used to hardcode,
      // and the hover/active darkening Mantine's Anchor doesn't have. It lives here rather than in
      // core-ui because it's the app's link look, not one wrapper's, so anything Anchor-based
      // picks it up. `inherit` restores the typography links had before Anchor:
      // Mantine's Text root pins font-size/line-height/weight, where the old link.module.css set
      // none of them, so an inline link in `Text size="sm"` would jump to `md`. The few links that
      // want explicit sizing pass `inherit={false}`. `underline` stays at Mantine's `hover`.
      Anchor: Anchor.extend({
        defaultProps: { inherit: true },
        classNames: { root: anchorClasses.root },
      }),
      // `variant="menu"` is the top-nav treatment for the logged-in user, and `variant="tinted"`
      // the facepile's (see avatar.module.css and the resolver above) — variants rather than
      // call-site `classNames`, which the locked Avatar doesn't expose.
      Avatar: Avatar.extend({
        classNames: { root: avatarClasses.root, placeholder: avatarClasses.placeholder },
      }),
      // Preserves today's core-ui Badge look once its hardcoded variant/color go. `variant="beta"`
      // is BetaBadge's yellow inset ring (see badge.module.css), for the same reason as Avatar's.
      Badge: Badge.extend({
        defaultProps: {
          variant: 'light',
          color: 'gray',
        },
        classNames: { root: badgeClasses.root, label: badgeClasses.label },
      }),
      // The whole card surface (see card.module.css): the elevated body background dark mode
      // needs, the legacy unclipped overflow, the draggable affordance, and the `error` variant
      // that replaced core-ui Card's bespoke boolean. `shadow`/`radius` were force-applied by
      // core-ui's wrapper; as defaultProps the value lives here instead. `radius` is now
      // overridable per call site, `shadow` is not — it's locked (LockedProps).
      Card: Card.extend({
        defaultProps: { shadow: 'xs', radius: 'md' },
        classNames: { root: cardClasses.root },
      }),
      // Card edges: Mantine's own Paper.css pins `--paper-border-color` to a raw gray-3, which no
      // call site can reach. This points it at the border token the design system documents
      // (see paper.module.css) — Card composes Paper, so it inherits the same edge.
      Paper: Paper.extend({
        classNames: { root: paperClasses.root },
      }),
      // The search-highlight fill, replacing the inline style HighlightedText put on a bare span.
      // It has to live on Highlight rather than Mark: Highlight always forwards an explicit `color`
      // to the Mark it renders (defaulting to Mantine's yellow), which would beat any Mark-level
      // defaultProp. A non-theme colour passes through `getMarkColor` verbatim into
      // `--mark-bg-light`/`--mark-bg-dark`, which Mantine applies inline.
      Highlight: Highlight.extend({
        defaultProps: { color: 'var(--mantine-primary-color-light-hover)' },
      }),
      // The active-item skin a call site used to apply with `className`, which core-ui locks (see
      // menu.module.css). Keyed on a `mod` data attribute rather than `variant`, since Mantine's
      // Menu.Item has none.
      Menu: Menu.extend({
        classNames: { item: menuClasses.item, itemIndicator: menuClasses.itemIndicator },
      }),
      // Modal chrome the design asked for everywhere (see modal.module.css): roomier `lg` header
      // and body padding — a real change, since Mantine's CSS falls back to `md` when the prop is
      // undefined — and a bold title, since Mantine's own is regular weight. The handful of call
      // sites that pass `padding="xl"` still win.
      // The chrome classes turn Mantine's single scrolling column into a fixed header, a scrolling
      // body and a fixed action row (EPD-1629). They live here rather than in core-ui's wrapper so
      // the imperative `@mantine/modals` prompts — which never touch that wrapper — get the same
      // chrome.
      Modal: Modal.extend({
        defaultProps: { padding: 'lg' },
        classNames: {
          body: modalClasses.body,
          close: modalClasses.close,
          content: modalClasses.content,
          header: modalClasses.header,
          root: modalClasses.root,
          title: modalClasses.title,
          overlay: modalClasses.overlay,
        },
      }),
      // Floating panels share one elevation. Every call site that set this passed `md` (one passed
      // `lg`), so it lands here rather than being restated per dropdown. Popover z-index must stay
      // above modal/overlay surfaces (200) so dropdowns remain visible inside full-page overlays.
      Popover: Popover.extend({
        defaultProps: { shadow: 'md', zIndex: getDefaultZIndex('popover') },
      }),
      HoverCard: HoverCard.extend({
        defaultProps: { shadow: 'md', zIndex: getDefaultZIndex('popover') },
      }),
      // The shared resting-border skin (see choice-control.module.css). Neither needs a radius
      // default: Mantine already ships `radius: 'sm'` (4px) on Checkbox, and Radio's radius is
      // its own size, i.e. the circle the design draws.
      Checkbox: Checkbox.extend({
        classNames: { input: choiceControlClasses.checkbox },
      }),
      Radio: Radio.extend({
        classNames: { radio: choiceControlClasses.radio },
      }),
      // System-level selectable-card look (see radio-card.module.css) so every
      // Radio.Card / Checkbox.Card matches the design without per-instance overrides.
      RadioCard: RadioCard.extend({
        classNames: { card: radioCardClasses.card },
      }),
      CheckboxCard: CheckboxCard.extend({
        classNames: { card: radioCardClasses.card },
      }),
      // System-level pill-chip look: outlined constant-size pill with the check slot hidden
      // and label border + padding pinned, so the checked state only recolors the pill
      // instead of resizing it.
      Chip: Chip.extend({
        defaultProps: { radius: 'xl', variant: 'outline' },
        styles: {
          iconWrapper: { display: 'none' },
          label: {
            borderWidth: 1,
            paddingLeft: 'var(--mantine-spacing-md)',
            paddingRight: 'var(--mantine-spacing-md)',
          },
        },
      }),
      // System-level input chrome. Every Mantine input component resolves theme props and
      // styles through the `Input` and `InputWrapper` keys before its own, so these two
      // entries cover TextInput, TextArea, Select, MultiSelect, NumberInput, PasswordInput,
      // DateInput, FileInput and the rest of the family in one place.
      //
      // `radius` is `sm` (4px) because Mantine's `defaultRadius` is `md` (8px) — right for a
      // card, too round for a 36px field. A defaultProp rather than a var so a call site can
      // still opt out. The border colour lives in input.module.css, which has to exclude the
      // error/success states.
      //
      // Disabled inputs keep the lighter gray-1 fill (Mantine's default is the global gray-2
      // `--mantine-color-disabled`) so a greyed-out field reads as clearly-but-softly
      // disabled. Scoped to inputs via the per-input var — deliberately NOT the global
      // `--mantine-color-disabled`, which other gray-2 fills (pill count, toggleable hover)
      // and non-input disabled surfaces rely on. There is no matching `--input-disabled-color`
      // override any more: the global disabled color is gray-5, which is both what the design
      // asks for and readable on the lighter fill.
      Input: Input.extend({
        defaultProps: { radius: 'sm' },
        classNames: { wrapper: inputClasses.wrapper, input: inputClasses.input },
        vars: () => ({
          wrapper: {},
          input: {
            '--input-disabled-bg': 'var(--mantine-color-gray-light-hover)',
          },
        }),
      }),
      // Labels are a fixed 12px in the design rather than tracking the input size (Mantine
      // derives `--input-label-size` from `size`, giving 14px at the default `sm`). Weight and
      // colour already match: Mantine's `--mantine-font-weight-medium` is 600, and the label
      // inherits `--mantine-color-text`. Set via `vars`, which merge over the component's own
      // resolver, so there's no specificity fight with Mantine's inline label sizing.
      InputWrapper: InputWrapper.extend({
        classNames: {
          description: inputClasses.description,
          error: inputClasses.error,
          success: inputClasses.success,
        },
        vars: () => ({
          label: { '--input-label-size': 'var(--mantine-font-size-xs)' },
          description: {},
          error: {},
          success: {},
        }),
      }),
      // Disabled buttons share the disabled input's softer gray-1 fill (above) so the two
      // read as the same disabled control. Mantine's Button has no per-button disabled-bg
      // var (it hardcodes the global gray-2 `--mantine-color-disabled`), so the fill is
      // overridden via a root class in button.module.css.
      //
      // Loading also sets `disabled`, so that fill covers in-flight buttons too. The
      // loader reads `--button-color` (white on filled) — theme `vars` merge after the
      // component resolver, so this is the same inline slot without a CSS fight.
      Button: Button.extend({
        classNames: { root: buttonClasses.root },
        vars: (_theme, props) =>
          props.loading
            ? { root: { '--button-color': 'var(--mantine-color-dimmed)' } }
            : { root: {} },
      }),
      // Drop the gray vertical separators between segments for a cleaner control.
      SegmentedControl: SegmentedControl.extend({
        defaultProps: { withItemsBorders: false },
      }),
      // Structural guards every Tabs instance needs (see tabs.module.css): horizontal rows
      // constrain for Scroller-backed overflow (no native scrollbar), except the `wizard` step
      // row, which wraps; vertical (sidebar) lists stay a single left-aligned column whose panel
      // can shrink. System-level layout, not a skin — which is why it lives here rather than behind
      // core-ui's locked `classNames`.
      // The `wizard` variant's markers ride on `tabSection`, so that slot is mapped too;
      // every rule keyed to it is variant-scoped, so no other tab row is touched.
      Tabs: Tabs.extend({
        classNames: {
          root: tabsClasses.root,
          list: tabsClasses.list,
          tab: tabsClasses.tab,
          tabLabel: tabsClasses.tabLabel,
          tabSection: tabsClasses.tabSection,
          panel: tabsClasses.panel,
        },
      }),
      // The `headless` variant (see stepper.module.css): step-content switching without the bar.
      // A variant rather than a core-ui prop, because hiding slots means `classNames` — which
      // core-ui locks.
      Stepper: Stepper.extend({
        classNames: {
          root: stepperClasses.root,
          step: stepperClasses.step,
          separator: stepperClasses.separator,
        },
      }),
      // Keeps MonospaceTextArea at its `sm` size (see text-area.module.css); its monospace font
      // comes from the `Input` entry's `[data-monospace]` rule, shared with every input.
      Textarea: Textarea.extend({
        classNames: { input: textAreaClasses.input },
      }),
    },
  });
}

export const Theme = (props: ThemeProps) => {
  const {
    children,
    fontFamily,
    fontFamilyMonospace,
    fontFamilyHeadings,
    communityThemeOverride,
    cssVariablesSelector,
    withCssVariables = true,
    withGlobalClasses = true,
  } = props;

  const theme = buildTheme({
    fontFamily,
    fontFamilyHeadings,
    fontFamilyMonospace,
    communityThemeOverride,
  });

  return (
    <MantineProvider
      theme={theme}
      defaultColorScheme="light"
      cssVariablesResolver={cssVariablesResolver}
      // Disable transitions under test so components that mount behind a
      // transition (e.g. the Combobox dropdown) render immediately instead of
      // staying `display: none` — otherwise Testing Library can't see their
      // contents (options, menu items) in the accessibility tree.
      env={process.env.NODE_ENV === 'test' ? 'test' : undefined}
      cssVariablesSelector={cssVariablesSelector}
      withCssVariables={withCssVariables}
      withGlobalClasses={withGlobalClasses}
    >
      {children}
    </MantineProvider>
  );
};
type HighlightThemeOther = typeof otherColors & {
  slug: string | null;
  privacyPolicyUrl: string | null;
};

export function getSlugFromThemeOther(themeOther: unknown): string | null {
  if (!isThemeOther(themeOther)) {
    return null;
  }

  return themeOther.slug;
}

export function getPrivacyPolicyUrlFromThemeOther(themeOther: unknown): string | null {
  if (!isThemeOther(themeOther)) {
    return null;
  }

  return themeOther.privacyPolicyUrl;
}

export function getHasCustomTheme(themeOther: unknown): boolean {
  if (!isThemeOther(themeOther)) {
    return false;
  }

  return !!themeOther.slug;
}

function isThemeOther(object: unknown): object is HighlightThemeOther {
  if (typeof object !== 'object' || object === null) {
    return false;
  }

  const obj = object as Record<string, unknown>;

  if (
    typeof obj['brand-color'] !== 'object' ||
    obj['brand-color'] === null ||
    typeof obj.text !== 'object' ||
    obj.text === null ||
    typeof obj.background !== 'object' ||
    obj.background === null ||
    obj.slug === undefined
  ) {
    return false;
  }

  const brandColor = obj['brand-color'] as Record<string, unknown>;
  if (
    typeof brandColor.yellow !== 'string' ||
    typeof brandColor.pink !== 'string' ||
    typeof brandColor.orange !== 'string' ||
    typeof brandColor.blue !== 'string' ||
    typeof brandColor.green !== 'string'
  ) {
    return false;
  }

  const text = obj.text as Record<string, unknown>;
  if (
    typeof text.primary !== 'string' ||
    typeof text.secondary !== 'string' ||
    typeof text.disabled !== 'string' ||
    typeof text.link !== 'string' ||
    typeof text.contrast !== 'string'
  ) {
    return false;
  }

  const background = obj.background as Record<string, unknown>;
  if (
    typeof background.primary !== 'string' ||
    typeof background.border !== 'string' ||
    typeof background.light !== 'string' ||
    typeof background.white !== 'string'
  ) {
    return false;
  }

  if (obj.slug !== null && typeof obj.slug !== 'string') {
    return false;
  }

  if (obj.privacyPolicyUrl === undefined) {
    return false;
  }

  if (obj.privacyPolicyUrl !== null && typeof obj.privacyPolicyUrl !== 'string') {
    return false;
  }

  return true;
}
